import { useMemo, useRef } from "react";
import { BaseChartColors } from "../../../../components/charts/constants";
import { ChartWrapper } from "../../../../components/ChartWrapper";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import { determineChartContent, hasNoData } from "../../utils";
import { useSpendByRevenueRangeData } from "./hooks/useGetSpendByRevenueRangeData";
import { mapSpendBreakdownResultToChartData } from "./utils";
import { BarAndLineChart } from "../../../../components/charts/BarAndLineChart/BarAndLineChart";
import { BarSize } from "../../../../components/charts/BarAndLineChart/types";
import { useCombinedFilters } from "../../../../hooks/useCombinedFilters";
import * as am4charts from "@amcharts/amcharts4/charts";
import { CHART_CONSTANTS } from "../constants";

const { baseId, chartTitle, tooltip, csvFieldMappings } =
  CHART_CONSTANTS.marketSizeByRevenue;

interface MarketSizeByRevenueChartProps {
  barSize?: BarSize;
  applyWorkspaceFiltersOnly?: boolean;
}

export function MarketSizeByRevenueChart({
  barSize,
  applyWorkspaceFiltersOnly = false,
}: MarketSizeByRevenueChartProps = {}) {
  const combinedFilters = useCombinedFilters(applyWorkspaceFiltersOnly);
  const chartRef = useRef<am4charts.XYChart>(null);
  const spendCategoriesWeights = useWorkspaceStore(
    (state) => state.draft?.configuration.tam.spendCategoryWeights
  );
  const localizeInstalls = useWorkspaceStore(
    (state) => state.draft?.configuration?.localizeInstalls ?? true
  );

  const { isLoading, spendBreakdownResults } = useSpendByRevenueRangeData({
    companyFilters: combinedFilters || {},
    customSpendCategory: spendCategoriesWeights && {
      name: "",
      categories: spendCategoriesWeights,
    },
    localizeInstalls,
  });

  const chartData = useMemo(() => {
    return mapSpendBreakdownResultToChartData(spendBreakdownResults).reverse();
  }, [spendBreakdownResults]);

  const noDataReturned = useMemo(
    () => hasNoData(isLoading, chartData),
    [isLoading, chartData]
  );

  const chartContent = useMemo(() => {
    return determineChartContent({
      isLoading,
      noDataReturned,
      baseId,
      chartToRender: (
        <BarAndLineChart
          id={`${baseId}-chart`}
          chartData={chartData}
          xAxisLabel="Spend (USD)"
          barSeriesColor={BaseChartColors.Green}
          lineSeriesName="NO. OF COMPANIES"
          barSeriesName="SPEND (USD)"
          barSize={barSize}
          ref={chartRef}
        />
      ),
    });
  }, [isLoading, noDataReturned, chartData, barSize]);

  return (
    <ChartWrapper
      id={`${baseId}-chart-wrapper`}
      title={chartTitle}
      tooltipTitle={tooltip}
      chart={chartContent}
      noData={noDataReturned}
      isLoading={isLoading}
      chartRef={chartRef}
      csvFieldMappings={csvFieldMappings}
    />
  );
}
